import React from "react";

interface FormFieldProps {
    label: string;
    error?: string;
    required?: boolean;
    children: React.ReactNode;
    className?: string;
    hint?: string;
}

/**
 * Reusable form field wrapper that surfaces validation errors inline.
 * Applies red border to the child input element when error is present.
 * Usage: wrap any <input>, <select>, <textarea> child inside this.
 */
export function FormField({ label, error, required = false, children, className = "", hint }: FormFieldProps) {
    // Inject error styling into the child element
    const child = React.Children.only(children) as React.ReactElement;
    const childWithError = React.cloneElement(child, {
        className: [
            child.props.className || "",
            error ? "!border-red-400 !ring-red-300 focus:!ring-red-400" : ""
        ].join(" ").trim(),
        "aria-invalid": !!error,
    });

    return (
        <div className={`flex flex-col gap-1 ${className}`}>
            <label className="block text-xs font-semibold text-slate-600 mb-0.5">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {childWithError}
            {hint && !error && (
                <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>
            )}
            {error && (
                <p className="text-[11px] text-red-500 mt-0.5 flex items-start gap-1" role="alert">
                    <span className="select-none">⚠</span>
                    <span>{error}</span>
                </p>
            )}
        </div>
    );
}
